/**
 * SpringBootArchitecture.jsx
 * Automotive Management System — Backend Architecture Infographic
 *
 * Clean Code Principles Applied:
 *  - Single Responsibility: each sub-component owns one concern
 *  - Open/Closed: data-driven rendering via CONSTANTS (no logic changes to add entities)
 *  - DRY: shared primitives (LayerRow, EntityCard, CodeBlock, etc.)
 *  - Meaningful naming: self-documenting identifiers
 *  - Separation of concerns: CONSTANTS / components / styles are distinct layers
 *
 * Design Patterns:
 *  - Strategy Pattern: tab panels rendered via lookup map
 *  - Factory Pattern: card/badge/pill creation from data config
 *  - Compound Component: ArchitectureLayer contains Label + Cards + Note
 *  - Container/Presentational split
 */

import { useState, useCallback } from "react";
import {
    Box, Chip, Tooltip, Typography, Tabs, Tab, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Divider, Stack,
} from "@mui/material";
import {
    FiSmartphone, FiMonitor, FiTool, FiLock, FiFilter,
    FiKey, FiZap, FiDatabase, FiMap, FiCpu,
    FiGlobe, FiRefreshCw, FiPackage, FiShield,
    FiAlertTriangle, FiLayers, FiCode, FiBox
} from "react-icons/fi";
import {
    SiSpringboot, SiFirebase, SiPostgresql, SiJsonwebtokens
} from "react-icons/si";
import {
    MdDirectionsCar, MdFactory, MdLocationOn, MdSecurity,
    MdSettings, MdApi, MdDataObject, MdArrowForward
} from "react-icons/md";
import { BsGearFill, BsCloudFill, BsDiagram3Fill } from "react-icons/bs";

// ─────────────────────────────────────────────────────────────
// DESIGN TOKENS — single source of truth for all styling
// ─────────────────────────────────────────────────────────────
const TOKENS = {
    colors: {
        bg:       "#050A14",
        surface:  "#0B1120",
        surface2: "#111827",
        border:   "#1E2D45",
        accent:   "#00D4FF",
        accent2:  "#FF6B35",
        accent3:  "#00FF94",
        accent4:  "#FFD60A",
        accent5:  "#BF5AF2",
        danger:   "#FF3B5C",
        firebase: "#FFA000",
        text:     "#E2EAF8",
        muted:    "#556B8A",
        white:    "#FFFFFF",
    },
    fonts: {
        mono:    "'JetBrains Mono', monospace",
        display: "'Bebas Neue', sans-serif",
        body:    "'Space Grotesk', sans-serif",
    },
    radius: { sm: "4px", md: "8px", lg: "12px" },
    spacing: { xs: 4, sm: 8, md: 16, lg: 24, xl: 40 },
};

const C = TOKENS.colors;
const F = TOKENS.fonts;

// ─────────────────────────────────────────────────────────────
// ARCHITECTURE DATA CONSTANTS
// ─────────────────────────────────────────────────────────────
const TECH_PILLS = [
    { label: "Spring Boot 3.2", color: C.accent },
    { label: "Java 17 LTS",     color: C.accent },
    { label: "Firebase Auth",   color: C.firebase },
    { label: "JWT RS256",       color: C.accent3 },
    { label: "JPA/Hibernate",   color: C.accent4 },
    { label: "Spring Security 6", color: C.accent5 },
    { label: "PostgreSQL 15",   color: C.accent },
    { label: "MapStruct",       color: C.accent2 },
    { label: "Lombok",          color: C.accent2 },
    { label: "OpenAPI 3.0",     color: C.accent4 },
];

const STATS = [
    { num: "3",  label: "Entities",    color: C.accent2 },
    { num: "6",  label: "Layers",      color: C.accent  },
    { num: "9",  label: "Controllers", color: C.accent3 },
    { num: "12", label: "Services",    color: C.accent4 },
    { num: "18", label: "Endpoints",   color: C.accent  },
    { num: "2",  label: "JWT Filters", color: C.accent5 },
];

const ARCHITECTURE_LAYERS = [
    {
        num: "01", icon: <FiGlobe />, name: "CLIENT LAYER", sub: "HTTP / HTTPS",
        color: C.accent2,
        cards: [
            { badge: "HTTP",  icon: <FiSmartphone />, name: "Mobile App",    type: "Android / iOS"   },
            { badge: "REST",  icon: <FiMonitor   />, name: "Web Client",    type: "React / Vue"     },
            { badge: "API",   icon: <FiTool      />, name: "Postman / cURL", type: "REST Consumer"  },
        ],
        note: "Bearer Token\nFirebase JWT\nHTTPS Only",
        detailKey: "client",
    },
    {
        num: "02", icon: <FiShield />, name: "SECURITY LAYER", sub: "Spring Security 6",
        color: C.accent5,
        cards: [
            { badge: "@Config", icon: <MdSettings  />, name: "SecurityConfig",    type: "FilterChain"       },
            { badge: "Filter",  icon: <SiFirebase  />, name: "FirebaseJwtFilter", type: "OncePerRequest"    },
            { badge: "Util",    icon: <FiKey       />, name: "JwtUtil",           type: "Token Validator"   },
            { badge: "Util",    icon: <SiFirebase  />, name: "FirebaseUtil",      type: "FirebaseApp Init"  },
            { badge: "@Bean",   icon: <FiGlobe     />, name: "CorsConfig",        type: "CORS Policy"       },
        ],
        note: "@PreAuthorize\nStateless JWT\nCORS + CSRF off",
        detailKey: "security",
    },
    {
        num: "03", icon: <MdApi />, name: "CONTROLLER LAYER", sub: "REST Endpoints",
        color: C.accent,
        cards: [
            { badge: "@REST", icon: <MdDirectionsCar />, name: "CarController",          type: "/api/v1/cars"          },
            { badge: "@REST", icon: <MdFactory       />, name: "ManufacturerController", type: "/api/v1/manufacturers" },
            { badge: "@REST", icon: <MdLocationOn    />, name: "LocationController",     type: "/api/v1/locations"     },
        ],
        note: "@Valid + DTO\nHTTP Responses\nError Handling",
        detailKey: "controller",
    },
    {
        num: "04", icon: <FiZap />, name: "SERVICE + DTO", sub: "Business Logic",
        color: C.accent3,
        cards: [
            { badge: "@Svc", icon: <MdDirectionsCar />, name: "CarService",          type: "@Transactional"  },
            { badge: "@Svc", icon: <MdFactory       />, name: "ManufacturerService", type: "@Transactional"  },
            { badge: "@Svc", icon: <MdLocationOn    />, name: "LocationService",     type: "@Transactional"  },
            { badge: "DTO",  icon: <FiPackage       />, name: "CarDTO",              type: "Request/Response" },
            { badge: "@Map", icon: <FiRefreshCw     />, name: "EntityMapper",        type: "MapStruct"        },
        ],
        note: "DTOs In/Out\nValidation\nBusiness Rules",
        detailKey: "service",
    },
    {
        num: "05", icon: <FiDatabase />, name: "REPOSITORY LAYER", sub: "Spring Data JPA",
        color: C.accent4,
        cards: [
            { badge: "@Repo", icon: <MdDirectionsCar />, name: "CarRepository",          type: "JpaRepository<Car,Long>"          },
            { badge: "@Repo", icon: <MdFactory       />, name: "ManufacturerRepository", type: "JpaRepository<Manufacturer,Long>" },
            { badge: "@Repo", icon: <MdLocationOn    />, name: "LocationRepository",     type: "JpaRepository<Location,Long>"     },
        ],
        note: "@Query JPQL\nPagination/Sort\nEntityGraph",
        detailKey: "repository",
    },
    {
        num: "06", icon: <FiBox />, name: "MODEL / DB", sub: "JPA + PostgreSQL",
        color: C.danger,
        cards: [
            { badge: "@Ent",   icon: <MdDirectionsCar />, name: "Car",          type: "cars table"          },
            { badge: "@Ent",   icon: <MdFactory       />, name: "Manufacturer", type: "manufacturers table" },
            { badge: "@Ent",   icon: <MdLocationOn    />, name: "Location",     type: "locations table"     },
            { badge: "@Super", icon: <FiLayers        />, name: "BaseEntity",   type: "@MappedSuperclass"   },
        ],
        note: "FK Constraints\nIndexes\nFlyway Migrate",
        detailKey: "model",
    },
];

const ENTITY_FIELDS = {
    Car: {
        icon: <MdDirectionsCar />, color: C.accent,
        fields: [
            { name: "id",           type: "Long",              tag: "PK"   },
            { name: "vin",          type: "String(17)",         tag: "IDX"  },
            { name: "licensePlate", type: "String(20)",         tag: "IDX"  },
            { name: "model",        type: "String(100)",        tag: ""     },
            { name: "year",         type: "Integer",            tag: ""     },
            { name: "color",        type: "String(50)",         tag: ""     },
            { name: "mileage",      type: "Double",             tag: ""     },
            { name: "engineType",   type: "EngineType(ENUM)",   tag: ""     },
            { name: "status",       type: "CarStatus(ENUM)",    tag: ""     },
            { name: "price",        type: "BigDecimal",         tag: ""     },
            { name: "manufacturer", type: "@ManyToOne",         tag: "FK"   },
            { name: "location",     type: "@ManyToOne",         tag: "FK"   },
            { name: "createdAt",    type: "LocalDateTime",      tag: "AUD"  },
            { name: "updatedAt",    type: "LocalDateTime",      tag: "AUD"  },
        ],
    },
    Manufacturer: {
        icon: <MdFactory />, color: C.accent2,
        fields: [
            { name: "id",           type: "Long",         tag: "PK"   },
            { name: "name",         type: "String(100)",  tag: "IDX"  },
            { name: "code",         type: "String(10)",   tag: "UNQ"  },
            { name: "country",      type: "String(50)",   tag: ""     },
            { name: "foundedYear",  type: "Integer",      tag: ""     },
            { name: "website",      type: "String(255)",  tag: ""     },
            { name: "logoUrl",      type: "String(500)",  tag: ""     },
            { name: "isActive",     type: "Boolean",      tag: ""     },
            { name: "contactEmail", type: "String(100)",  tag: ""     },
            { name: "phoneNumber",  type: "String(20)",   tag: ""     },
            { name: "cars",         type: "@OneToMany",   tag: ""     },
            { name: "createdAt",    type: "LocalDateTime",tag: "AUD"  },
        ],
    },
    Location: {
        icon: <MdLocationOn />, color: C.accent3,
        fields: [
            { name: "id",           type: "Long",              tag: "PK"  },
            { name: "name",         type: "String(100)",       tag: ""    },
            { name: "code",         type: "String(10)",        tag: "UNQ" },
            { name: "addressLine1", type: "String(200)",       tag: ""    },
            { name: "city",         type: "String(100)",       tag: "IDX" },
            { name: "state",        type: "String(50)",        tag: ""    },
            { name: "country",      type: "String(50)",        tag: ""    },
            { name: "postalCode",   type: "String(20)",        tag: ""    },
            { name: "latitude",     type: "Double",            tag: ""    },
            { name: "longitude",    type: "Double",            tag: ""    },
            { name: "capacity",     type: "Integer",           tag: ""    },
            { name: "locationType", type: "LocationType(ENUM)",tag: ""    },
            { name: "cars",         type: "@OneToMany",        tag: ""    },
            { name: "createdAt",    type: "LocalDateTime",     tag: "AUD" },
        ],
    },
};

const RELATIONSHIPS = [
    { from: "Car",          fromColor: C.accent,  annotation: "@ManyToOne",  to: "Manufacturer", toColor: C.accent2, fk: "manufacturer_id", type: "N:1", fetch: "LAZY" },
    { from: "Car",          fromColor: C.accent,  annotation: "@ManyToOne",  to: "Location",     toColor: C.accent3, fk: "location_id",     type: "N:1", fetch: "LAZY" },
    { from: "Manufacturer", fromColor: C.accent2, annotation: "@OneToMany",  to: "List<Car>",    toColor: C.accent,  fk: "mappedBy",        type: "1:N", fetch: "LAZY" },
    { from: "Location",     fromColor: C.accent3, annotation: "@OneToMany",  to: "List<Car>",    toColor: C.accent,  fk: "mappedBy",        type: "1:N", fetch: "LAZY" },
];

const BASE_ENTITY_FIELDS = [
    { name: "id: Long",              annotation: "@Id @GeneratedValue(IDENTITY)",    color: C.accent4 },
    { name: "createdAt: LocalDateTime", annotation: "@CreatedDate @Column(updatable=false)", color: C.accent2 },
    { name: "updatedAt: LocalDateTime", annotation: "@LastModifiedDate",             color: C.accent2 },
    { name: "createdBy: String",     annotation: "@CreatedBy — stores Firebase UID", color: C.accent3 },
    { name: "version: Long",         annotation: "@Version — Optimistic Locking",    color: C.accent5 },
];

const SECURITY_STEPS = [
    { num: "1", icon: <FiSmartphone />, name: "Client Login",    desc: "Firebase SDK\nsignInWithEmail\nor OAuth2/Google",       color: C.accent2   },
    { num: "2", icon: <SiFirebase   />, name: "Firebase Auth",   desc: "Google Firebase\nauthenticates user\nreturns ID Token", color: C.firebase  },
    { num: "3", icon: <FiGlobe      />, name: "API Request",     desc: "Authorization:\nBearer <jwt>\nHTTPS to API",           color: C.accent5   },
    { num: "4", icon: <FiFilter     />, name: "JWT Filter",      desc: "FirebaseJwtFilter\nextract + verify\ntoken via SDK",   color: C.accent5   },
    { num: "5", icon: <FiShield     />, name: "Auth Context",    desc: "Set SecurityContext\nUsernamePasswordAuth\n+ Roles",   color: C.accent3   },
    { num: "6", icon: <FiDatabase   />, name: "Business Logic",  desc: "Controller→Service\n→Repository\n→PostgreSQL",        color: C.accent    },
    { num: "7", icon: <FiPackage    />, name: "Response",        desc: "JSON ResponseDTO\n200/201/404/401\nor error body",     color: C.accent3   },
];

const JWT_SECTIONS = [
    {
        title: "HEADER", color: C.accent2,
        code: `{\n  "alg": "RS256",\n  "typ": "JWT",\n  "kid": "<key-id>"\n}`,
    },
    {
        title: "PAYLOAD (Claims)", color: C.accent5,
        code: `{\n  "iss": "firebase-project",\n  "aud": "<project-id>",\n  "uid": "user-uid-123",\n  "email": "user@co.com",\n  "role": "ADMIN",\n  "exp": 1718000000\n}`,
    },
    {
        title: "SIGNATURE (RS256)", color: C.accent3,
        code: `// Signed by Firebase\n// with Google private key\n// Verified via JWKS:\nhttps://www.googleapis.com\n/service_accounts/v1/jwk\n/securetoken@system...`,
    },
];

const METHOD_COLORS = {
    GET:    C.accent3,
    POST:   C.accent,
    PUT:    C.accent4,
    DELETE: C.danger,
};

const ENDPOINTS = [
    { section: "🚗  CAR — /api/v1/cars", sectionColor: C.accent },
    { method: "GET",    path: "/api/v1/cars",          desc: "List all cars (paginated)",  body: "Pageable params",   response: "Page<CarResponseDTO>",    role: "Any"   },
    { method: "GET",    path: "/api/v1/cars/{id}",     desc: "Get car by ID",              body: "—",                  response: "CarResponseDTO",          role: "Any"   },
    { method: "GET",    path: "/api/v1/cars/vin/{vin}",desc: "Find car by VIN",            body: "—",                  response: "CarResponseDTO",          role: "Any"   },
    { method: "POST",   path: "/api/v1/cars",          desc: "Create new car",             body: "CarRequestDTO",      response: "CarResponseDTO (201)",     role: "ADMIN" },
    { method: "PUT",    path: "/api/v1/cars/{id}",     desc: "Update car fields",          body: "CarRequestDTO",      response: "CarResponseDTO",          role: "ADMIN" },
    { method: "DELETE", path: "/api/v1/cars/{id}",     desc: "Soft-delete car",            body: "—",                  response: "204 No Content",          role: "ADMIN" },
    { section: "🏭  MANUFACTURERS — /api/v1/manufacturers", sectionColor: C.accent2 },
    { method: "GET",    path: "/api/v1/manufacturers",           desc: "List all (paginated)",     body: "page, active",          response: "Page<ManufacturerResponseDTO>", role: "Any"   },
    { method: "GET",    path: "/api/v1/manufacturers/{id}",      desc: "Get by ID",                body: "—",                     response: "ManufacturerResponseDTO",      role: "Any"   },
    { method: "GET",    path: "/api/v1/manufacturers/{id}/cars", desc: "Get cars by manufacturer", body: "—",                     response: "List<CarResponseDTO>",          role: "Any"   },
    { method: "POST",   path: "/api/v1/manufacturers",           desc: "Create manufacturer",      body: "ManufacturerRequestDTO",response: "ManufacturerResponseDTO (201)", role: "ADMIN" },
    { method: "PUT",    path: "/api/v1/manufacturers/{id}",      desc: "Update manufacturer",      body: "ManufacturerRequestDTO",response: "ManufacturerResponseDTO",      role: "ADMIN" },
    { method: "DELETE", path: "/api/v1/manufacturers/{id}",      desc: "Deactivate",               body: "—",                     response: "204 No Content",               role: "ADMIN" },
    { section: "📍  LOCATIONS — /api/v1/locations", sectionColor: C.accent3 },
    { method: "GET",    path: "/api/v1/locations",          desc: "List all locations",    body: "page, city, type",    response: "Page<LocationResponseDTO>", role: "Any"   },
    { method: "GET",    path: "/api/v1/locations/{id}",     desc: "Get location by ID",    body: "—",                   response: "LocationResponseDTO",       role: "Any"   },
    { method: "GET",    path: "/api/v1/locations/{id}/cars",desc: "Get cars at location",  body: "—",                   response: "List<CarResponseDTO>",      role: "Any"   },
    { method: "POST",   path: "/api/v1/locations",          desc: "Create location",       body: "LocationRequestDTO",  response: "LocationResponseDTO (201)", role: "ADMIN" },
    { method: "PUT",    path: "/api/v1/locations/{id}",     desc: "Update location",       body: "LocationRequestDTO",  response: "LocationResponseDTO",       role: "ADMIN" },
    { method: "DELETE", path: "/api/v1/locations/{id}",     desc: "Delete location",       body: "—",                   response: "204 No Content",            role: "ADMIN" },
];

const PACKAGE_TREE_LEFT = [
    { text: "com.automotive.management/",    color: C.accent4, bold: true  },
    { text: "├── config/",                   color: C.accent2, bold: false },
    { text: "│   ├── SecurityConfig.java",   color: C.accent5, bold: false },
    { text: "│   ├── FirebaseConfig.java",   color: C.firebase,bold: false },
    { text: "│   ├── CorsConfig.java",       color: C.accent,  bold: false },
    { text: "│   ├── AuditConfig.java",      color: C.accent,  bold: false },
    { text: "│   └── OpenApiConfig.java",    color: C.accent,  bold: false },
    { text: "├── controller/",               color: C.accent2, bold: false },
    { text: "│   ├── CarController.java",    color: C.accent,  bold: false },
    { text: "│   ├── ManufacturerController.java", color: C.accent, bold: false },
    { text: "│   └── LocationController.java",     color: C.accent, bold: false },
    { text: "├── service/",                  color: C.accent2, bold: false },
    { text: "│   ├── CarService.java",       color: C.muted,   bold: false },
    { text: "│   ├── CarServiceImpl.java",   color: C.accent,  bold: false },
    { text: "│   ├── ManufacturerService.java",    color: C.muted,  bold: false },
    { text: "│   ├── ManufacturerServiceImpl.java",color: C.accent, bold: false },
    { text: "│   ├── LocationService.java",  color: C.muted,   bold: false },
    { text: "│   └── LocationServiceImpl.java",    color: C.accent, bold: false },
    { text: "├── repository/",               color: C.accent2, bold: false },
    { text: "│   ├── CarRepository.java",    color: C.accent,  bold: false },
    { text: "│   ├── ManufacturerRepository.java", color: C.accent, bold: false },
    { text: "│   └── LocationRepository.java",     color: C.accent, bold: false },
    { text: "├── model/",                    color: C.accent2, bold: false },
    { text: "│   ├── Car.java",              color: C.accent,  bold: false },
    { text: "│   ├── Manufacturer.java",     color: C.accent2, bold: false },
    { text: "│   ├── Location.java",         color: C.accent3, bold: false },
    { text: "│   └── BaseEntity.java",       color: C.accent4, bold: false },
];

const PACKAGE_TREE_RIGHT = [
    { text: "├── dto/",                      color: C.accent2, bold: false },
    { text: "│   ├── request/",              color: C.accent3, bold: false },
    { text: "│   │   ├── CarRequestDTO.java",       color: C.accent3, bold: false },
    { text: "│   │   ├── ManufacturerRequestDTO.java",color: C.accent3,bold: false },
    { text: "│   │   └── LocationRequestDTO.java",  color: C.accent3, bold: false },
    { text: "│   └── response/",             color: C.accent3, bold: false },
    { text: "│       ├── CarResponseDTO.java",      color: C.accent3, bold: false },
    { text: "│       └── LocationResponseDTO.java", color: C.accent3, bold: false },
    { text: "├── mapper/",                   color: C.accent2, bold: false },
    { text: "│   ├── CarMapper.java",        color: C.accent,  bold: false },
    { text: "│   └── ManufacturerMapper.java",      color: C.accent, bold: false },
    { text: "├── security/",                 color: C.accent2, bold: false },
    { text: "│   ├── filter/",               color: C.accent5, bold: false },
    { text: "│   │   └── FirebaseJwtFilter.java",   color: C.accent5, bold: false },
    { text: "│   ├── util/",                 color: C.accent5, bold: false },
    { text: "│   │   ├── JwtUtil.java",      color: C.accent5, bold: false },
    { text: "│   │   └── FirebaseUtil.java", color: C.accent5, bold: false },
    { text: "│   └── UserPrincipal.java",    color: C.accent,  bold: false },
    { text: "├── exception/",                color: C.accent2, bold: false },
    { text: "│   ├── GlobalExceptionHandler.java",  color: C.danger, bold: false },
    { text: "│   └── ResourceNotFoundException.java",color: C.danger,bold: false },
    { text: "├── enums/",                    color: C.accent2, bold: false },
    { text: "│   ├── CarStatus.java",        color: C.accent4, bold: false },
    { text: "│   ├── EngineType.java",       color: C.accent4, bold: false },
    { text: "│   └── LocationType.java",     color: C.accent4, bold: false },
    { text: "├── util/",                     color: C.accent2, bold: false },
    { text: "│   ├── SecurityContextUtil.java",     color: C.accent, bold: false },
    { text: "│   └── PaginationUtil.java",   color: C.accent,  bold: false },
    { text: "└── AutomotiveManagementApplication.java", color: C.accent3, bold: true },
];

const CODE_SNIPPETS = [
    {
        title: "🚗  Car.java (@Entity)",
        color: C.accent,
        lines: [
            "@Entity @Table(name = \"cars\",",
            "  indexes = {@Index(columnList = \"vin\"),",
            "             @Index(columnList = \"status\")})",
            "@Data @NoArgsConstructor",
            "public class Car extends BaseEntity {",
            "",
            "  @Column(unique=true, length=17, nullable=false)",
            "  private String vin;",
            "",
            "  @Enumerated(EnumType.STRING)",
            "  private CarStatus status;",
            "",
            "  @Enumerated(EnumType.STRING)",
            "  private EngineType engineType;",
            "",
            "  @Column(precision=12, scale=2)",
            "  private BigDecimal price;",
            "",
            "  @ManyToOne(fetch = FetchType.LAZY)",
            "  @JoinColumn(name = \"manufacturer_id\")",
            "  private Manufacturer manufacturer;",
            "",
            "  @ManyToOne(fetch = FetchType.LAZY)",
            "  @JoinColumn(name = \"location_id\")",
            "  private Location location;",
            "}",
        ],
    },
    {
        title: "🗄️  CarRepository.java",
        color: C.accent4,
        lines: [
            "@Repository",
            "public interface CarRepository",
            "    extends JpaRepository<Car, Long> {",
            "",
            "  Optional<Car> findByVin(String vin);",
            "",
            "  Optional<Car> findByLicensePlate(",
            "    String licensePlate);",
            "",
            "  Page<Car> findByStatus(",
            "    CarStatus status, Pageable pageable);",
            "",
            "  List<Car> findByManufacturerId(",
            "    Long manufacturerId);",
            "",
            "  @Query(\"SELECT c FROM Car c WHERE\"",
            "    + \" c.year BETWEEN :from AND :to\")",
            "  List<Car> findByYearRange(",
            "    @Param(\"from\") int from,",
            "    @Param(\"to\")   int to);",
            "",
            "  @EntityGraph(attributePaths =",
            "    {\"manufacturer\", \"location\"})",
            "  Optional<Car> findWithDetailsById(",
            "    Long id);",
            "}",
        ],
    },
    {
        title: "🛡️  GlobalExceptionHandler.java",
        color: C.danger,
        lines: [
            "@RestControllerAdvice",
            "public class GlobalExceptionHandler {",
            "",
            "  @ExceptionHandler(",
            "    ResourceNotFoundException.class)",
            "  @ResponseStatus(NOT_FOUND)",
            "  public ErrorResponse handleNotFound(",
            "      ResourceNotFoundException ex) {",
            "    return new ErrorResponse(",
            "      404, ex.getMessage(),",
            "      LocalDateTime.now());",
            "  }",
            "",
            "  @ExceptionHandler(",
            "    FirebaseAuthException.class)",
            "  @ResponseStatus(UNAUTHORIZED)",
            "  public ErrorResponse handleFirebase(",
            "      FirebaseAuthException ex) {",
            "    return new ErrorResponse(",
            "      401, \"Invalid Firebase token\",",
            "      LocalDateTime.now());",
            "  }",
            "",
            "  @ExceptionHandler(",
            "    MethodArgumentNotValidException.class)",
            "  @ResponseStatus(BAD_REQUEST)",
            "  public ErrorResponse handleValidation(",
            "      MethodArgumentNotValidException ex) {",
            "    // collect field errors ...",
            "  }",
            "}",
        ],
    },
];

// ─────────────────────────────────────────────────────────────
// SHARED STYLE HELPERS  (SX factory functions = clean, DRY)
// ─────────────────────────────────────────────────────────────
const sx = {
    surface: (border = C.border) => ({
        background: C.surface,
        border: `1px solid ${border}`,
        borderRadius: TOKENS.radius.lg,
    }),
    monoText: (color = C.accent, size = 11) => ({
        fontFamily: F.mono,
        fontSize: size,
        color,
    }),
    flex: (gap = 8, align = "center") => ({
        display: "flex",
        alignItems: align,
        gap,
    }),
    badge: (color) => ({
        fontFamily: F.mono,
        fontSize: 9,
        fontWeight: 700,
        color: "#000",
        bgcolor: color,
        borderRadius: "3px",
        px: 0.7,
        py: 0.2,
        letterSpacing: 0.5,
        textTransform: "uppercase",
        lineHeight: 1.4,
        display: "inline-block",
    }),
};

// ─────────────────────────────────────────────────────────────
// PRIMITIVE COMPONENTS
// ─────────────────────────────────────────────────────────────

/** MonoText — renders monospace text with design-system color */
const MonoText = ({ children, color = C.accent, size = 11, bold = false, sx: sxProp = {} }) => (
    <Typography
        sx={{
            fontFamily: F.mono,
            fontSize: size,
            color,
            fontWeight: bold ? 700 : 400,
            lineHeight: 1.5,
            ...sxProp,
        }}
    >
        {children}
    </Typography>
);

/** TechPill — colored label badge */
const TechPill = ({ label, color }) => (
    <Box
        sx={{
            px: 1.5, py: 0.5,
            border: `1px solid ${color}40`,
            borderRadius: "20px",
            bgcolor: `${color}18`,
            fontFamily: F.mono,
            fontSize: 10,
            fontWeight: 700,
            color,
            letterSpacing: 0.5,
            whiteSpace: "nowrap",
        }}
    >
        {label}
    </Box>
);

/** StatBox — single KPI tile */
const StatBox = ({ num, label, color }) => (
    <Box
        sx={{
            ...sx.surface(),
            p: 1.5,
            textAlign: "center",
            flex: 1,
            transition: "all 0.25s",
            "&:hover": { borderColor: color, transform: "translateY(-2px)" },
        }}
    >
        <Typography sx={{ fontFamily: F.display, fontSize: 28, color, lineHeight: 1 }}>
            {num}
        </Typography>
        <MonoText color={C.muted} size={9} sx={{ textTransform: "uppercase", letterSpacing: 1, mt: 0.5 }}>
            {label}
        </MonoText>
    </Box>
);

/** SectionHeader — numbered section title */
const SectionHeader = ({ num, title }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Typography sx={{ fontFamily: F.display, fontSize: 48, color: C.border, lineHeight: 1 }}>
            {num}
        </Typography>
        <Typography sx={{ fontFamily: F.display, fontSize: 26, color: C.white, letterSpacing: 2 }}>
            {title}
        </Typography>
        <Box sx={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${C.border}, transparent)` }} />
    </Box>
);

/** CodeBlock — dark syntax display */
const CodeBlock = ({ lines, title, accentColor = C.accent }) => (
    <Box sx={{ ...sx.surface(accentColor), overflow: "hidden" }}>
        {title && (
            <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${C.border}` }}>
                <MonoText color={accentColor} size={10} bold>{title}</MonoText>
            </Box>
        )}
        <Box sx={{ p: 2, background: C.surface2 }}>
            {lines.map((line, i) => (
                <MonoText key={i} color={line.startsWith("//") || line.startsWith("*") ? C.muted : C.accent3} size={9}>
                    {line || "\u00a0"}
                </MonoText>
            ))}
        </Box>
    </Box>
);

/** MethodBadge — HTTP verb chip */
const MethodBadge = ({ method }) => (
    <Box
        sx={{
            px: 1, py: 0.3,
            borderRadius: "4px",
            bgcolor: `${METHOD_COLORS[method]}22`,
            border: `1px solid ${METHOD_COLORS[method]}55`,
            fontFamily: F.mono,
            fontSize: 9,
            fontWeight: 700,
            color: METHOD_COLORS[method],
            textAlign: "center",
            minWidth: 48,
        }}
    >
        {method}
    </Box>
);

/** TagBadge — PK / FK / IDX / UNQ */
const TagBadge = ({ tag }) => {
    const tagColors = { PK: C.accent4, FK: C.accent5, IDX: C.accent3, UNQ: C.accent3, AUD: C.muted };
    const col = tagColors[tag] || C.muted;
    if (!tag) return null;
    return (
        <Box sx={{ ...sx.badge(col), fontSize: 8, color: tag === "AUD" ? C.muted : "#000", bgcolor: tag === "AUD" ? `${col}22` : col, border: `1px solid ${col}55` }}>
            {tag}
        </Box>
    );
};

// ─────────────────────────────────────────────────────────────
// FEATURE COMPONENTS
// ─────────────────────────────────────────────────────────────

/** ArchCard — a single card within an architecture layer */
const ArchCard = ({ card, layerColor, selected, onClick }) => (
    <Tooltip title={`${card.name} · ${card.type}`} placement="top" arrow>
        <Box
            onClick={onClick}
            sx={{
                background: selected ? C.surface : C.bg,
                border: `1px solid ${selected ? layerColor : C.border}`,
                borderRadius: TOKENS.radius.md,
                p: 1.5,
                cursor: "pointer",
                minWidth: 110,
                textAlign: "center",
                transition: "all 0.2s",
                boxShadow: selected ? `0 0 16px ${layerColor}40` : "none",
                "&:hover": {
                    transform: "translateY(-3px)",
                    borderColor: layerColor,
                    boxShadow: `0 8px 24px rgba(0,0,0,0.4), 0 0 16px ${layerColor}30`,
                },
                position: "relative",
                flexShrink: 0,
            }}
        >
            <Box sx={{ ...sx.badge(C.accent4), position: "absolute", top: -8, left: "50%", transform: "translateX(-50%)", whiteSpace: "nowrap" }}>
                {card.badge}
            </Box>
            <Box sx={{ fontSize: 18, color: layerColor, mb: 0.5, mt: 0.5 }}>{card.icon}</Box>
            <MonoText color={layerColor} size={10} bold>{card.name}</MonoText>
            <MonoText color={C.muted} size={9}>{card.type}</MonoText>
        </Box>
    </Tooltip>
);

/** ArchitectureLayer — one full layer row */
const ArchitectureLayer = ({ layer, selectedCard, onCardClick }) => (
    <Box sx={{ display: "grid", gridTemplateColumns: "160px 1fr 110px", gap: 0, mb: "2px" }}>
        {/* Label */}
        <Box sx={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            background: C.surface, border: `1px solid ${layer.color}55`, borderRight: "none",
            p: 1.5, gap: 0.5,
        }}>
            <Box sx={{ fontSize: 22, color: layer.color }}>{layer.icon}</Box>
            <Typography sx={{ fontFamily: F.display, fontSize: 28, color: layer.color, lineHeight: 1, opacity: 0.3 }}>
                {layer.num}
            </Typography>
            <MonoText color={layer.color} size={9} bold sx={{ textAlign: "center", letterSpacing: 1 }}>
                {layer.name}
            </MonoText>
            <MonoText color={C.muted} size={8} sx={{ textAlign: "center" }}>{layer.sub}</MonoText>
        </Box>

        {/* Cards */}
        <Box sx={{
            display: "flex", gap: 1.5, p: 1.5, pl: 3,
            background: C.surface2, border: `1px solid ${layer.color}20`,
            flexWrap: "wrap", alignItems: "center",
        }}>
            {layer.cards.map((card) => (
                <ArchCard
                    key={card.name}
                    card={card}
                    layerColor={layer.color}
                    selected={selectedCard === `${layer.num}-${card.name}`}
                    onClick={() => onCardClick(`${layer.num}-${card.name}`, layer, card)}
                />
            ))}
        </Box>

        {/* Note */}
        <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "center",
            background: C.surface, border: `1px solid ${layer.color}30`,
            borderLeft: "none", p: 1.5,
        }}>
            <MonoText color={C.muted} size={8} sx={{ textAlign: "center", whiteSpace: "pre-line" }}>
                {layer.note}
            </MonoText>
        </Box>
    </Box>
);

/** EntityCard — one entity box in the ER diagram */
const EntityCard = ({ entityName, entityData }) => (
    <Box sx={{ ...sx.surface(entityData.color + "55"), overflow: "hidden", flex: 1 }}>
        {/* Header */}
        <Box sx={{
            display: "flex", alignItems: "center", gap: 1.5, p: 2,
            background: `linear-gradient(135deg, ${entityData.color}20, transparent)`,
            borderBottom: `1px solid ${entityData.color}40`,
        }}>
            <Box sx={{ fontSize: 22, color: entityData.color }}>{entityData.icon}</Box>
            <Box>
                <Typography sx={{ fontFamily: F.mono, fontSize: 14, fontWeight: 700, color: C.white }}>
                    {entityName}
                </Typography>
                <MonoText color={C.muted} size={9}>&lt;&lt;@Entity&gt;&gt;</MonoText>
            </Box>
        </Box>
        {/* Fields */}
        {entityData.fields.map((f) => (
            <Box
                key={f.name}
                sx={{
                    display: "flex", alignItems: "center", gap: 1, px: 2, py: 0.7,
                    borderBottom: `1px solid ${C.border}55`,
                    "&:nth-of-type(odd)": { background: C.surface },
                    "&:nth-of-type(even)": { background: C.surface2 },
                }}
            >
                <MonoText color={C.text} size={10} sx={{ flex: 1 }}>{f.name}</MonoText>
                {f.tag && <TagBadge tag={f.tag} />}
                <MonoText color={C.accent4} size={9} sx={{ textAlign: "right", minWidth: 80 }}>{f.type}</MonoText>
            </Box>
        ))}
    </Box>
);

/** SecurityStepCard — one step in the firebase flow */
const SecurityStepCard = ({ step }) => (
    <Box sx={{
        ...sx.surface(`${step.color}55`),
        p: 2, textAlign: "center", flex: 1, minWidth: 0,
        background: `linear-gradient(135deg, ${step.color}10, ${C.surface})`,
    }}>
        <Typography sx={{ fontFamily: F.display, fontSize: 20, color: step.color, lineHeight: 1 }}>
            {step.num}
        </Typography>
        <Box sx={{ fontSize: 20, color: step.color, my: 0.5 }}>{step.icon}</Box>
        <MonoText color={step.color} size={9} bold sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
            {step.name}
        </MonoText>
        <MonoText color={C.muted} size={8} sx={{ mt: 0.5, whiteSpace: "pre-line" }}>{step.desc}</MonoText>
    </Box>
);

/** PackageTreeColumn */
const PackageTreeColumn = ({ lines }) => (
    <Box sx={{ ...sx.surface(), p: 2, flex: 1, background: C.surface2 }}>
        {lines.map((line, i) => (
            <MonoText key={i} color={line.color} size={9} bold={line.bold} sx={{ lineHeight: 1.9 }}>
                {line.text || "\u00a0"}
            </MonoText>
        ))}
    </Box>
);

// ─────────────────────────────────────────────────────────────
// TAB PANEL COMPONENTS  (Strategy Pattern — one component per tab)
// ─────────────────────────────────────────────────────────────

/** ArchitecturePanel */
const ArchitecturePanel = () => {
    const [selectedCard, setSelectedCard] = useState(null);
    const [detailLayer, setDetailLayer]   = useState(null);
    const [detailCard, setDetailCard]     = useState(null);

    const handleCardClick = useCallback((key, layer, card) => {
        if (selectedCard === key) {
            setSelectedCard(null); setDetailLayer(null); setDetailCard(null);
        } else {
            setSelectedCard(key); setDetailLayer(layer); setDetailCard(card);
        }
    }, [selectedCard]);

    return (
        <Box>
            {/* Stats */}
            <Box sx={{ display: "flex", gap: 1.5, mb: 3 }}>
                {STATS.map((s) => <StatBox key={s.label} {...s} />)}
            </Box>

            {/* Layer rows */}
            <Box sx={{ mb: 2 }}>
                {ARCHITECTURE_LAYERS.map((layer) => (
                    <ArchitectureLayer
                        key={layer.num}
                        layer={layer}
                        selectedCard={selectedCard}
                        onCardClick={handleCardClick}
                    />
                ))}
            </Box>

            {/* Detail panel */}
            {detailCard && (
                <Box sx={{
                    ...sx.surface(detailLayer.color + "55"),
                    p: 3, mt: 2,
                    animation: "slideUp 0.25s ease",
                    "@keyframes slideUp": { from: { opacity: 0, transform: "translateY(12px)" }, to: { opacity: 1, transform: "translateY(0)" } },
                }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, pb: 2, borderBottom: `1px solid ${C.border}` }}>
                        <Box sx={{ fontSize: 28, color: detailLayer.color }}>{detailCard.icon}</Box>
                        <Box>
                            <MonoText color={detailLayer.color} size={16} bold>{detailCard.name}.java</MonoText>
                            <MonoText color={C.muted} size={11}>{detailCard.type} · Layer {detailLayer.num} — {detailLayer.name}</MonoText>
                        </Box>
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
                        <Box>
                            <MonoText color={C.muted} size={10} bold sx={{ textTransform: "uppercase", letterSpacing: 1, mb: 1 }}>Layer Info</MonoText>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                {[
                                    ["Badge",   detailCard.badge],
                                    ["Type",    detailCard.type],
                                    ["Layer",   detailLayer.name],
                                    ["Package", detailLayer.sub],
                                    ["Note",    detailLayer.note.replace(/\n/g, " · ")],
                                ].map(([k, v]) => (
                                    <Box key={k} sx={{ display: "flex", gap: 2, py: 0.5, borderBottom: `1px solid ${C.border}60` }}>
                                        <MonoText color={C.muted} size={10} sx={{ width: 60 }}>{k}</MonoText>
                                        <MonoText color={detailLayer.color} size={10}>{v}</MonoText>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                        <Box>
                            <MonoText color={C.muted} size={10} bold sx={{ textTransform: "uppercase", letterSpacing: 1, mb: 1 }}>Annotations</MonoText>
                            {[
                                `@Component / @${detailCard.badge.replace("@", "")}`,
                                `@RequiredArgsConstructor (Lombok)`,
                                `@Slf4j (logging)`,
                                `@Transactional (Service layer)`,
                                `@Valid / @PreAuthorize (Controller)`,
                            ].map((ann) => (
                                <Box key={ann} sx={{ display: "flex", gap: 1, py: 0.4, borderBottom: `1px solid ${C.border}40` }}>
                                    <MonoText color={C.accent2} size={10}>{ann}</MonoText>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Legend */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 2, p: 2, ...sx.surface() }}>
                {ARCHITECTURE_LAYERS.map((l) => (
                    <Box key={l.num} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: l.color }} />
                        <MonoText color={C.muted} size={10}>{l.name}</MonoText>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

/** EntityPanel */
const EntityPanel = () => (
    <Box>
        <SectionHeader num="02" title="ENTITY RELATIONSHIP DIAGRAM" />

        {/* Entity cards */}
        <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "flex-start" }}>
            {Object.entries(ENTITY_FIELDS).map(([name, data]) => (
                <EntityCard key={name} entityName={name} entityData={data} />
            ))}
        </Box>

        {/* BaseEntity */}
        <Box sx={{ ...sx.surface(`${C.accent4}55`), p: 2, mb: 2 }}>
            <MonoText color={C.accent4} size={11} bold sx={{ mb: 1.5 }}>
                🧱  BaseEntity — @MappedSuperclass (inherited by ALL entities)
            </MonoText>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                {BASE_ENTITY_FIELDS.map((f) => (
                    <Box key={f.name} sx={{ display: "flex", gap: 1, alignItems: "center", py: 0.5, px: 1.5, bgcolor: C.surface2, borderRadius: TOKENS.radius.sm, border: `1px solid ${C.border}` }}>
                        <MonoText color={f.color} size={10} bold>{f.name}</MonoText>
                        <MonoText color={C.muted} size={9}>— {f.annotation}</MonoText>
                    </Box>
                ))}
            </Box>
        </Box>

        {/* Relationships */}
        <MonoText color={C.accent} size={12} bold sx={{ mb: 1.5, textTransform: "uppercase", letterSpacing: 1 }}>
            🔗 Entity Relationships
        </MonoText>
        <TableContainer component={Paper} sx={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: TOKENS.radius.lg }}>
            <Table size="small">
                <TableHead>
                    <TableRow sx={{ "& th": { fontFamily: F.mono, fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 1, borderColor: C.border, bgcolor: C.surface } }}>
                        {["From", "Annotation", "To", "FK Column", "Type", "Fetch"].map((h) => (
                            <TableCell key={h}>{h}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {RELATIONSHIPS.map((r, i) => (
                        <TableRow key={i} sx={{ "&:hover td": { bgcolor: C.surface2 }, "& td": { borderColor: `${C.border}55`, py: 1 } }}>
                            <TableCell><MonoText color={r.fromColor} size={10} bold>{r.from}</MonoText></TableCell>
                            <TableCell><MonoText color={C.accent5} size={10}>{r.annotation}</MonoText></TableCell>
                            <TableCell><MonoText color={r.toColor} size={10} bold>{r.to}</MonoText></TableCell>
                            <TableCell><MonoText color={C.accent4} size={10}>{r.fk}</MonoText></TableCell>
                            <TableCell><MonoText color={C.accent3} size={10} bold>{r.type}</MonoText></TableCell>
                            <TableCell><MonoText color={C.muted} size={10}>{r.fetch}</MonoText></TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    </Box>
);

/** SecurityPanel */
const SecurityPanel = () => (
    <Box>
        <SectionHeader num="03" title="FIREBASE + JWT SECURITY FLOW" />

        {/* Flow steps */}
        <Box sx={{ display: "flex", gap: 1, mb: 3, alignItems: "stretch" }}>
            {SECURITY_STEPS.map((step, i) => (
                <Box key={step.num} sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
                    <SecurityStepCard step={step} />
                    {i < SECURITY_STEPS.length - 1 && (
                        <Box sx={{ color: C.muted, fontSize: 16, mx: 0.5, flexShrink: 0 }}>
                            <MdArrowForward />
                        </Box>
                    )}
                </Box>
            ))}
        </Box>

        {/* Code blocks: SecurityConfig + Filter + JwtUtil */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2, mb: 3 }}>
            <CodeBlock
                title="⚙️  SecurityConfig.java"
                accentColor={C.accent5}
                lines={[
                    "@Configuration",
                    "@EnableWebSecurity",
                    "@EnableMethodSecurity",
                    "public class SecurityConfig {",
                    "",
                    "  @Bean",
                    "  public SecurityFilterChain filterChain(",
                    "      HttpSecurity http) throws Exception {",
                    "    http",
                    "      .csrf(disable())",
                    "      .sessionManagement(s ->",
                    "        s.sessionCreationPolicy(STATELESS))",
                    "      .authorizeHttpRequests(auth -> auth",
                    "        .requestMatchers(\"/api/v1/auth/**\")",
                    "          .permitAll()",
                    "        .anyRequest().authenticated())",
                    "      .addFilterBefore(",
                    "        firebaseJwtFilter,",
                    "        UsernamePasswordAuthFilter.class);",
                    "    return http.build();",
                    "  }",
                    "}",
                ]}
            />
            <CodeBlock
                title="🔥  FirebaseJwtFilter.java"
                accentColor={C.firebase}
                lines={[
                    "@Component",
                    "public class FirebaseJwtFilter",
                    "    extends OncePerRequestFilter {",
                    "",
                    "  @Override",
                    "  protected void doFilterInternal(",
                    "      HttpServletRequest request,",
                    "      HttpServletResponse response,",
                    "      FilterChain chain) throws ... {",
                    "",
                    "    String token = extractToken(request);",
                    "    if (token != null) {",
                    "      FirebaseToken decoded =",
                    "        FirebaseAuth.getInstance()",
                    "          .verifyIdToken(token);",
                    "      var auth = new",
                    "        UsernamePasswordAuthToken(",
                    "          decoded.getUid(), null, roles);",
                    "      SecurityContextHolder",
                    "        .getContext()",
                    "        .setAuthentication(auth);",
                    "    }",
                    "    chain.doFilter(request, response);",
                    "  }",
                    "}",
                ]}
            />
            <CodeBlock
                title="🗝️  JwtUtil + FirebaseConfig"
                accentColor={C.accent3}
                lines={[
                    "// JwtUtil.java",
                    "@Component",
                    "public class JwtUtil {",
                    "",
                    "  public FirebaseToken verifyToken(",
                    "      String idToken) {",
                    "    return FirebaseAuth",
                    "      .getInstance()",
                    "      .verifyIdToken(idToken);",
                    "  }",
                    "",
                    "  public List<GrantedAuthority> getRoles(",
                    "      FirebaseToken token) {",
                    "    Object role = token",
                    "      .getClaims().get(\"role\");",
                    "    return List.of(new",
                    "      SimpleGrantedAuthority(",
                    "        \"ROLE_\" + role));",
                    "  }",
                    "}",
                    "",
                    "// FirebaseConfig.java",
                    "@Bean FirebaseApp init():",
                    "  FileInputStream svc = ...",
                    "  FirebaseOptions opts = ...",
                    "  FirebaseApp.initializeApp(opts)",
                ]}
            />
        </Box>

        {/* JWT Token Anatomy */}
        <MonoText color={C.accent} size={12} bold sx={{ mb: 1.5, textTransform: "uppercase", letterSpacing: 1 }}>
            🗝️ Firebase JWT Token Anatomy (RS256)
        </MonoText>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1.4fr 1fr", gap: 2 }}>
            {JWT_SECTIONS.map((s) => (
                <Box key={s.title} sx={{ ...sx.surface(s.color + "55"), overflow: "hidden" }}>
                    <Box sx={{ px: 2, py: 1, bgcolor: `${s.color}15`, borderBottom: `1px solid ${s.color}40` }}>
                        <MonoText color={s.color} size={10} bold>{s.title}</MonoText>
                    </Box>
                    <Box sx={{ p: 2, background: C.surface2 }}>
                        <MonoText color={s.color} size={9} sx={{ whiteSpace: "pre", fontFamily: F.mono }}>
                            {s.code}
                        </MonoText>
                    </Box>
                </Box>
            ))}
        </Box>
    </Box>
);

/** EndpointsPanel */
const EndpointsPanel = () => (
    <Box>
        <SectionHeader num="04" title="REST API ENDPOINT REFERENCE" />
        <MonoText color={C.muted} size={10} sx={{ mb: 2 }}>
            Base URL: https://api.automotive.com/api/v1  ·  All endpoints require Firebase Bearer Token
        </MonoText>
        <TableContainer component={Paper} sx={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: TOKENS.radius.lg }}>
            <Table size="small">
                <TableHead>
                    <TableRow sx={{ "& th": { fontFamily: F.mono, fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 1, borderColor: C.border, bgcolor: C.surface } }}>
                        {["Method", "Endpoint", "Description", "Request Body", "Response", "Role"].map((h) => (
                            <TableCell key={h}>{h}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {ENDPOINTS.map((row, i) => {
                        if (row.section) {
                            return (
                                <TableRow key={i}>
                                    <TableCell colSpan={6} sx={{ bgcolor: `${row.sectionColor}10`, borderColor: `${row.sectionColor}30`, py: 1 }}>
                                        <MonoText color={row.sectionColor} size={10} bold>{row.section}</MonoText>
                                    </TableCell>
                                </TableRow>
                            );
                        }
                        return (
                            <TableRow key={i} sx={{ "&:hover td": { bgcolor: C.surface2 }, "& td": { borderColor: `${C.border}55`, py: 0.8 } }}>
                                <TableCell><MethodBadge method={row.method} /></TableCell>
                                <TableCell><MonoText color={C.accent} size={9}>{row.path}</MonoText></TableCell>
                                <TableCell><MonoText color={C.text} size={9}>{row.desc}</MonoText></TableCell>
                                <TableCell><MonoText color={C.muted} size={9}>{row.body}</MonoText></TableCell>
                                <TableCell><MonoText color={C.accent3} size={9}>{row.response}</MonoText></TableCell>
                                <TableCell>
                                    <MonoText color={row.role === "ADMIN" ? C.danger : C.muted} size={9} bold={row.role === "ADMIN"}>
                                        {row.role === "ADMIN" ? "🔐 ADMIN" : "✅ Any"}
                                    </MonoText>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    </Box>
);

/** PackagesPanel */
const PackagesPanel = () => (
    <Box>
        <SectionHeader num="05" title="PACKAGE STRUCTURE + CODE SNIPPETS" />

        {/* Package trees */}
        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <PackageTreeColumn lines={PACKAGE_TREE_LEFT} />
            <PackageTreeColumn lines={PACKAGE_TREE_RIGHT} />
        </Box>

        {/* Code snippets */}
        <MonoText color={C.accent} size={12} bold sx={{ mb: 1.5, textTransform: "uppercase", letterSpacing: 1 }}>
            Key Code Snippets
        </MonoText>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
            {CODE_SNIPPETS.map((s) => (
                <CodeBlock key={s.title} title={s.title} accentColor={s.color} lines={s.lines} />
            ))}
        </Box>
    </Box>
);

// ─────────────────────────────────────────────────────────────
// TAB CONFIG  (Strategy / Factory: maps tabId → component)
// ─────────────────────────────────────────────────────────────
const TABS = [
    { id: "architecture", label: "Architecture Layers", icon: <BsDiagram3Fill />, Panel: ArchitecturePanel },
    { id: "entities",     label: "Entity Diagram",      icon: <MdDataObject   />, Panel: EntityPanel       },
    { id: "security",     label: "Security Flow",       icon: <MdSecurity     />, Panel: SecurityPanel     },
    { id: "endpoints",    label: "REST Endpoints",      icon: <MdApi          />, Panel: EndpointsPanel    },
    { id: "packages",     label: "Package Structure",   icon: <FiCode         />, Panel: PackagesPanel     },
];

// ─────────────────────────────────────────────────────────────
// ROOT COMPONENT
// ─────────────────────────────────────────────────────────────
 function SpringBootArchitecture() {
    const [activeTab, setActiveTab] = useState(0);

    const handleTabChange = useCallback((_, newValue) => setActiveTab(newValue), []);

    const { Panel } = TABS[activeTab];

    return (
        <Box sx={{ background: C.bg, minHeight: "100vh", fontFamily: F.body, color: C.text }}>

            {/* ── HEADER ── */}
            <Box sx={{
                position: "relative", px: 5, pt: 4, pb: 3,
                borderBottom: `1px solid ${C.border}`,
                background: `linear-gradient(135deg, ${C.bg} 0%, #0B1525 100%)`,
                overflow: "hidden",
                "&::before": {
                    content: '""', position: "absolute",
                    top: -60, right: -60, width: 300, height: 300,
                    borderRadius: "50%",
                    background: `radial-gradient(circle, ${C.accent}12 0%, transparent 70%)`,
                },
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                    <Box sx={{ ...sx.surface(C.accent), px: 1.5, py: 0.5 }}>
                        <MonoText color={C.accent} size={10} bold sx={{ letterSpacing: 2 }}>
                            ARCHITECTURE BLUEPRINT v1.0
                        </MonoText>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, fontSize: 18, color: C.muted }}>
                        <SiSpringboot color="#6DB33F" />
                        <SiFirebase color={C.firebase} />
                        <SiPostgresql color="#336791" />
                        <SiJsonwebtokens color={C.accent3} />
                    </Box>
                </Box>

                <Typography sx={{ fontFamily: F.display, fontSize: "clamp(28px,4vw,56px)", color: C.white, letterSpacing: 3, lineHeight: 1, mb: 0.5 }}>
                    AUTOMOTIVE <Box component="span" sx={{ color: C.accent }}>MANAGEMENT</Box> SYSTEM
                </Typography>
                <MonoText color={C.muted} size={11} sx={{ mb: 2.5 }}>
                    // Spring Boot 3.x · Java 17 · Firebase Auth · JWT RS256 · PostgreSQL · Spring Security 6
                </MonoText>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {TECH_PILLS.map((p) => <TechPill key={p.label} {...p} />)}
                </Box>
            </Box>

            {/* ── TABS ── */}
            <Box sx={{ background: C.surface, borderBottom: `1px solid ${C.border}` }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        px: 4,
                        "& .MuiTab-root": {
                            fontFamily: F.mono, fontSize: 11, fontWeight: 700,
                            color: C.muted, textTransform: "uppercase", letterSpacing: 1,
                            minHeight: 48, gap: 0.5,
                        },
                        "& .Mui-selected": { color: `${C.accent} !important` },
                        "& .MuiTabs-indicator": { background: C.accent, height: 2 },
                    }}
                >
                    {TABS.map((tab) => (
                        <Tab key={tab.id} label={tab.label} icon={tab.icon} iconPosition="start" />
                    ))}
                </Tabs>
            </Box>

            {/* ── PANEL CONTENT ── */}
            <Box sx={{ px: 5, py: 4, maxWidth: "100%", overflow: "hidden" }}>
                <Panel />
            </Box>
        </Box>
    );
}

export default SpringBootArchitecture;
